<template>
  <div class="max-w-3xl mx-auto space-y-6 animate-[fadeIn_0.5s_ease-out]">
    <!-- 头部标题 -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl font-black text-slate-900 flex items-center">
        <IconHistory :size="24" class-name="w-6 h-6 mr-3 text-[#F2C94C]" /> 历史记录
      </h2>
      <button
        v-if="canClear && currentListLength > 0"
        class="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1"
        @click="showClearConfirm = true"
      >
        <IconTrash2 :size="14" class-name="w-3.5 h-3.5" />
        清空
      </button>
    </div>

    <!-- 标签导航 -->
    <div class="flex gap-6 border-b border-slate-100 overflow-x-auto no-scrollbar relative">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="pb-3 text-sm font-bold transition-all border-b-2 -mb-[1px] flex items-center gap-2 whitespace-nowrap"
        :class="activeTab === tab.id ? 'border-[#F2C94C] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'"
        @click="handleTabClick(tab.id)"
      >
        <component :is="tab.icon" :size="16" class-name="w-4 h-4" />
        {{ tab.label }}
        <span
          class="ml-1 px-1.5 py-0.5 rounded-full text-[10px]"
          :class="activeTab === tab.id ? 'bg-[#F2C94C]/20 text-[#F2C94C]' : 'bg-slate-100 text-slate-400'"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <!-- 搜索与时间筛选 -->
    <div class="flex flex-col sm:flex-row gap-3 sticky top-0 z-10 bg-[#F8FAFC] py-2">
      <div class="relative flex-1">
        <IconSearch :size="16" class-name="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="搜索话题、专家、场景..."
          class="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
        />
      </div>
      <div class="flex gap-2">
        <button
          v-for="tf in timeFilters"
          :key="tf"
          class="px-3 py-2 text-xs font-medium rounded-lg border transition-all"
          :class="timeFilter === tf ? 'bg-[#F2C94C] text-white border-[#F2C94C]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'"
          @click="timeFilter = tf"
        >
          {{ tf === 'all' ? '全部' : tf === '7d' ? '最近7天' : '最近30天' }}
        </button>
      </div>
    </div>

    <div class="py-2">
      <transition name="tab-switch" mode="out-in">
        <div :key="activeTab" class="space-y-3">
          <!-- 学一学 -->
          <template v-if="activeTab === 'ask'">
            <div
              v-for="record in filteredStudyRecords"
              :key="record.id"
              class="bg-white p-4 rounded-lg border border-slate-100 shadow-sm hover:shadow-md hover:border-[#F2C94C]/40 cursor-pointer transition-all group relative"
              @click="handleNavigate('home', { topicId: record.topicId })"
            >
              <div class="flex items-start justify-between pr-8">
                <div class="flex-1">
                  <h4 class="font-bold text-slate-900 mb-2 group-hover:text-[#F2C94C] transition-colors">
                    {{ record.topicTitle }}
                  </h4>
                  <div class="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                    <span class="flex items-center gap-1">
                      <IconClock :size="12" class-name="w-3 h-3" />
                      {{ formatTime(record.timestamp) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <IconFileText :size="12" class-name="w-3 h-3" />
                      阅读 {{ formatDuration(record.duration) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <IconAward :size="12" class-name="w-3 h-3" />
                      {{ record.expertName }} · {{ record.expertTitle }}
                    </span>
                  </div>
                </div>
                <IconChevronRight :size="20" class-name="w-5 h-5 text-slate-300 group-hover:text-[#F2C94C] absolute right-4 top-1/2 -translate-y-1/2" />
              </div>
              <!-- 更多菜单 -->
              <div class="absolute top-3 right-3">
                <button
                  class="p-1.5 text-slate-300 hover:text-slate-500 hover:bg-slate-50 rounded-md transition-colors"
                  @click.stop="toggleMenu(record.id)"
                >
                  <IconMoreHorizontal :size="16" class-name="w-4 h-4" />
                </button>
                <div
                  v-if="openMenuId === record.id"
                  class="absolute right-0 top-full mt-1 w-24 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-20"
                >
                  <button
                    class="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-50 flex items-center gap-1.5"
                    @click.stop="handleDelete(record.id)"
                  >
                    <IconTrash2 :size="12" class-name="w-3 h-3" /> 删除
                  </button>
                </div>
              </div>
            </div>
            <div v-if="filteredStudyRecords.length === 0" class="text-center py-16 text-slate-400">
              <IconBookOpen :size="48" class-name="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p class="text-sm">
                暂无学习记录，去
                <span class="text-[#F2C94C] cursor-pointer hover:underline" @click="handleNavigate('home', {})">学一学</span>
                看看吧
              </p>
            </div>
          </template>

          <!-- 练一练 -->
          <template v-if="activeTab === 'practice'">
            <div
              v-for="record in filteredPracticeRecords"
              :key="record.id"
              class="bg-white p-4 rounded-lg border border-slate-100 shadow-sm hover:shadow-md hover:border-[#F2C94C]/40 cursor-pointer transition-all group relative"
              @click="handleNavigate('practice', { scenarioId: record.scenarioId })"
            >
              <div class="flex items-start justify-between pr-8">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <h4 class="font-bold text-slate-900 group-hover:text-[#F2C94C] transition-colors">
                      {{ record.scenarioTitle.split('】')[1] || record.scenarioTitle }}
                    </h4>
                    <span
                      v-if="record.isCorrect"
                      class="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 text-[10px] font-bold rounded"
                    >
                      <IconCircleCheckBig :size="12" class-name="w-3 h-3" /> 决策正确
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-bold rounded"
                    >
                      <IconCircleX :size="12" class-name="w-3 h-3" /> 红区警告
                    </span>
                  </div>
                  <div class="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                    <span class="flex items-center gap-1">
                      <IconClock :size="12" class-name="w-3 h-3" />
                      {{ formatTime(record.timestamp) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <IconTarget :size="12" class-name="w-3 h-3" />
                      答题用时 {{ formatDuration(record.timeSpent) }}
                    </span>
                  </div>
                  <div v-if="record.impact" class="flex items-center gap-3 mt-2">
                    <div
                      v-for="item in impactItems(record.impact)"
                      :key="item.label"
                      class="flex items-center gap-1 text-[11px]"
                    >
                      <IconTrendingUp
                        v-if="item.value > 0"
                        :size="12"
                        class-name="w-3 h-3 text-green-500"
                      />
                      <IconTrendingDown
                        v-else-if="item.value < 0"
                        :size="12"
                        class-name="w-3 h-3 text-red-500"
                      />
                      <IconMinus
                        v-else
                        :size="12"
                        class-name="w-3 h-3 text-slate-300"
                      />
                      <span :class="item.value > 0 ? 'text-green-600' : item.value < 0 ? 'text-red-600' : 'text-slate-400'">
                        {{ item.label }}{{ item.value > 0 ? `+${item.value}` : item.value }}
                      </span>
                    </div>
                  </div>
                </div>
                <IconChevronRight :size="20" class-name="w-5 h-5 text-slate-300 group-hover:text-[#F2C94C] absolute right-4 top-1/2 -translate-y-1/2" />
              </div>
              <!-- 更多菜单 -->
              <div class="absolute top-3 right-3">
                <button
                  class="p-1.5 text-slate-300 hover:text-slate-500 hover:bg-slate-50 rounded-md transition-colors"
                  @click.stop="toggleMenu(record.id)"
                >
                  <IconMoreHorizontal :size="16" class-name="w-4 h-4" />
                </button>
                <div
                  v-if="openMenuId === record.id"
                  class="absolute right-0 top-full mt-1 w-24 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-20"
                >
                  <button
                    class="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-50 flex items-center gap-1.5"
                    @click.stop="handleDelete(record.id)"
                  >
                    <IconTrash2 :size="12" class-name="w-3 h-3" /> 删除
                  </button>
                </div>
              </div>
            </div>
            <div v-if="filteredPracticeRecords.length === 0" class="text-center py-16 text-slate-400">
              <IconTarget :size="48" class-name="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p class="text-sm">
                暂无演练记录，去
                <span class="text-[#F2C94C] cursor-pointer hover:underline" @click="handleNavigate('practice', {})">练一练</span>
                开始挑战吧
              </p>
            </div>
          </template>

          <!-- 聊一聊 -->
          <template v-if="activeTab === 'chat'">
            <div
              v-for="item in filteredChatRecords"
              :key="item.id"
              class="bg-white p-5 rounded-lg border border-slate-100 shadow-sm hover:shadow-md hover:border-[#F2C94C]/40 cursor-pointer transition-all group relative"
              @click="handleReloadChat(item.context)"
            >
              <div class="flex items-start justify-between pr-8">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-[#F2C94C]/10 rounded-xl flex items-center justify-center">
                    <IconMessageSquare :size="20" class-name="w-5 h-5 text-[#F2C94C]" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="font-bold text-slate-900 truncate">{{ item.query }}</h4>
                    <p class="text-[11px] text-slate-400 mt-1">{{ formatTime(item.timestamp) }}</p>
                    <p class="text-[11px] text-slate-500 mt-2 line-clamp-2 max-w-md">
                      {{ item.aiResponse.slice(0, 40) }}{{ item.aiResponse.length > 40 ? '...' : '' }}
                    </p>
                    <div v-if="item.chatHistory && item.chatHistory.length > 2" class="flex items-center gap-2 text-[11px] text-slate-400 mt-2">
                      <IconChartNoAxesColumn :size="12" class-name="w-3 h-3" />
                      <span>共 {{ Math.floor(item.chatHistory.length / 2) }} 轮对话</span>
                    </div>
                  </div>
                </div>
                <IconChevronRight :size="20" class-name="w-5 h-5 text-slate-300 group-hover:text-[#F2C94C] absolute right-4 top-1/2 -translate-y-1/2" />
              </div>
              <!-- 更多菜单 -->
              <div class="absolute top-3 right-3">
                <button
                  class="p-1.5 text-slate-300 hover:text-slate-500 hover:bg-slate-50 rounded-md transition-colors"
                  @click.stop="toggleMenu(item.id)"
                >
                  <IconMoreHorizontal :size="16" class-name="w-4 h-4" />
                </button>
                <div
                  v-if="openMenuId === item.id"
                  class="absolute right-0 top-full mt-1 w-24 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-20"
                >
                  <button
                    class="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-50 flex items-center gap-1.5"
                    @click.stop="handleDelete(item.id)"
                  >
                    <IconTrash2 :size="12" class-name="w-3 h-3" /> 删除
                  </button>
                </div>
              </div>
            </div>
            <div v-if="filteredChatRecords.length === 0" class="text-center py-16 text-slate-400">
              <IconMessageSquare :size="48" class-name="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p class="text-sm">
                暂无诊断记录，去
                <span class="text-[#F2C94C] cursor-pointer hover:underline" @click="handleNavigate('diagnose-start', {})">聊一聊</span>
                开始深度咨询吧
              </p>
            </div>
          </template>
        </div>
      </transition>
    </div>

    <!-- 点击外部关闭菜单 -->
    <div
      v-if="openMenuId"
      class="fixed inset-0 z-10"
      @click="openMenuId = null"
    />

    <!-- 清空确认弹窗 -->
    <transition name="modal">
      <div
        v-if="showClearConfirm"
        class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        @click="showClearConfirm = false"
      >
        <div
          class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
          @click.stop
        >
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <IconTriangleAlert :size="20" class-name="w-5 h-5 text-red-500" />
            </div>
            <h3 class="text-lg font-bold text-slate-900">确认清空？</h3>
          </div>
          <p class="text-sm text-slate-500 mb-6">
            清空后，当前分类下的所有记录将无法恢复，是否继续？
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
              @click="showClearConfirm = false"
            >
              取消
            </button>
            <button
              class="flex-1 py-2.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
              @click="handleClear"
            >
              确认清空
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  IconBookOpen,
  IconActivity,
  IconTarget,
  IconHistory,
  IconChevronRight,
  IconAward,
  IconClock,
  IconMessageSquare,
  IconCircleCheckBig,
  IconCircleX,
  IconFileText,
  IconChartNoAxesColumn,
  IconSearch,
  IconMoreHorizontal,
  IconTrash2,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconTriangleAlert,
} from '../icons'

// 示例数据 - 学一学
const DEMO_STUDY_RECORDS = [
  {
    id: 'study-001',
    topicId: 'topic-retention',
    topicTitle: '核心骨干突然沉默，疑似有离职倾向',
    expertName: '张建国',
    expertTitle: '前华为组织发展专家',
    action: 'view',
    timestamp: Date.now() - 86400000 * 2,
    duration: 180,
  },
  {
    id: 'study-002',
    topicId: 'topic-performance',
    topicTitle: '如何提升团队执行力',
    expertName: '李慕华',
    expertTitle: '360高级副总裁/资深教练',
    action: 'bookmark',
    timestamp: Date.now() - 86400000 * 5,
    duration: 240,
  },
  {
    id: 'study-003',
    topicId: 'topic-communication',
    topicTitle: '跨部门协作低效',
    expertName: '陈志远',
    expertTitle: '字节跳动前HRBP负责人',
    action: 'view',
    timestamp: Date.now() - 86400000 * 7,
    duration: 120,
  },
]

// 示例数据 - 练一练
const DEMO_PRACTICE_RECORDS = [
  {
    id: 'practice-001',
    scenarioId: 'scenario-001',
    scenarioTitle: '【常规管理】你安排了一项重要任务，但下属反馈消极，进度缓慢。',
    category: '常规管理',
    selectedOption: 'option-b',
    isCorrect: true,
    impact: { morale: 15, efficiency: 20, retention: -5 },
    timestamp: Date.now() - 86400000 * 1,
    timeSpent: 45,
  },
  {
    id: 'practice-002',
    scenarioId: 'scenario-002',
    scenarioTitle: '【绩效宣贯】团队对新绩效考核标准不理解，抵触情绪严重。',
    category: '绩效宣贯',
    selectedOption: 'option-a',
    isCorrect: false,
    impact: { morale: -15, efficiency: -10, retention: 20 },
    timestamp: Date.now() - 86400000 * 3,
    timeSpent: 62,
  },
  {
    id: 'practice-003',
    scenarioId: 'scenario-003',
    scenarioTitle: '【离职预警】核心员工突然提出离职，HR希望你协助挽留。',
    category: '离职预警',
    selectedOption: 'option-c',
    isCorrect: true,
    impact: { morale: 25, efficiency: 10, retention: -20 },
    timestamp: Date.now() - 86400000 * 6,
    timeSpent: 88,
  },
  {
    id: 'practice-004',
    scenarioId: 'scenario-004',
    scenarioTitle: '【执行力差】项目进度严重滞后，团队成员互相推诿责任。',
    category: '常规管理',
    selectedOption: 'option-b',
    isCorrect: true,
    impact: { morale: 10, efficiency: 25, retention: 0 },
    timestamp: Date.now() - 86400000 * 10,
    timeSpent: 55,
  },
]

// 示例数据 - 聊一聊
const DEMO_CHAT_RECORDS = [
  {
    id: 'chat-001',
    query: '团队核心骨干流失风险高，如何建立有效的留人机制？',
    aiResponse: '当前团队核心骨干流失风险已达临界点，建议立即开启非业务导向的一对一深度面谈...',
    timestamp: Date.now() - 86400000 * 1,
    context: {
      businessStage: '快速扩张期',
      teamStatus: '人心浮动中',
      leadershipStyle: '强势结果导向',
      managementMode: '互联网敏捷模式',
      span: 12,
      levels: 3,
      composition: ['研发', '产品', '运营'],
      pressure: 8,
      decisionMode: '民主决策',
    },
    isDeepDiagnosis: true,
    chatHistory: [
      { id: '1', role: 'user', content: '团队核心骨干流失风险高，如何建立有效的留人机制？' },
      { id: '2', role: 'ai', content: '理解您的困境。快速扩张期人心浮动，建议从三个维度入手...' },
      { id: '3', role: 'user', content: '具体应该如何进行一对一沟通？' },
      { id: '4', role: 'ai', content: '建议采用"3F倾听法"：Fact（事实）、Feeling（感受）、Focus（聚焦）...' },
    ],
  },
  {
    id: 'chat-002',
    query: '跨部门协作效率低下，如何建立有效的协同机制？',
    aiResponse: '跨部门协作低效的核心是利益不对齐，建议建立"三方对齐"机制...',
    timestamp: Date.now() - 86400000 * 4,
    context: {
      businessStage: '稳定成熟期',
      teamStatus: '内耗血栓化',
      leadershipStyle: '温和关系导向',
      managementMode: '华为实战模式',
      span: 8,
      levels: 2,
      composition: ['研发', '测试'],
      pressure: 5,
      decisionMode: '层级审批',
    },
    isDeepDiagnosis: true,
  },
]

export default {
  name: 'HistoryView',
  components: {
    IconBookOpen,
    IconActivity,
    IconTarget,
    IconHistory,
    IconChevronRight,
    IconAward,
    IconClock,
    IconMessageSquare,
    IconCircleCheckBig,
    IconCircleX,
    IconFileText,
    IconChartNoAxesColumn,
    IconSearch,
    IconMoreHorizontal,
    IconTrash2,
    IconTrendingUp,
    IconTrendingDown,
    IconMinus,
    IconTriangleAlert,
  },
  props: {
    history: {
      type: Array,
      default: () => []
    },
    studyRecords: {
      type: Array,
      default: () => []
    },
    practiceRecords: {
      type: Array,
      default: () => []
    },
    onReloadChat: {
      type: Function,
      default: null
    },
    onNavigate: {
      type: Function,
      default: null
    },
    onDeleteStudyRecord: {
      type: Function,
      default: null
    },
    onDeletePracticeRecord: {
      type: Function,
      default: null
    },
    onDeleteChatRecord: {
      type: Function,
      default: null
    },
    onClearStudyRecords: {
      type: Function,
      default: null
    },
    onClearPracticeRecords: {
      type: Function,
      default: null
    },
    onClearChatRecords: {
      type: Function,
      default: null
    },
  },
  data() {
    return {
      activeTab: 'ask',
      isMounted: false,
      searchQuery: '',
      timeFilter: 'all',
      openMenuId: null,
      showClearConfirm: false,
      timeFilters: ['all', '7d', '30d'],
    }
  },
  computed: {
    studyRecordsMerged() {
      return this.isMounted && this.studyRecords && this.studyRecords.length > 0
        ? this.studyRecords
        : DEMO_STUDY_RECORDS
    },
    practiceRecordsMerged() {
      return this.isMounted && this.practiceRecords && this.practiceRecords.length > 0
        ? this.practiceRecords
        : DEMO_PRACTICE_RECORDS
    },
    chatRecordsMerged() {
      return this.isMounted && this.history && this.history.length > 0
        ? this.history.filter(h => h.isDeepDiagnosis)
        : DEMO_CHAT_RECORDS
    },
    tabs() {
      return [
        { id: 'ask', label: '学一学', icon: 'IconBookOpen', count: this.studyRecordsMerged.length },
        { id: 'practice', label: '练一练', icon: 'IconTarget', count: this.practiceRecordsMerged.length },
        { id: 'chat', label: '聊一聊', icon: 'IconActivity', count: this.chatRecordsMerged.length },
      ]
    },
    filteredStudyRecords() {
      return this.studyRecordsMerged.filter(r => {
        if (!this.isWithinTimeFilter(r.timestamp)) return false
        if (!this.searchQuery.trim()) return true
        const q = this.searchQuery.toLowerCase()
        return r.topicTitle.toLowerCase().includes(q) || r.expertName.toLowerCase().includes(q)
      })
    },
    filteredPracticeRecords() {
      return this.practiceRecordsMerged.filter(r => {
        if (!this.isWithinTimeFilter(r.timestamp)) return false
        if (!this.searchQuery.trim()) return true
        const q = this.searchQuery.toLowerCase()
        return r.scenarioTitle.toLowerCase().includes(q) || r.category.toLowerCase().includes(q)
      })
    },
    filteredChatRecords() {
      return this.chatRecordsMerged.filter(r => {
        if (!this.isWithinTimeFilter(r.timestamp)) return false
        if (!this.searchQuery.trim()) return true
        const q = this.searchQuery.toLowerCase()
        return r.query.toLowerCase().includes(q) || r.aiResponse.toLowerCase().includes(q)
      })
    },
    canClear() {
      return this.activeTab === 'ask' || this.activeTab === 'practice' || this.activeTab === 'chat'
    },
    currentListLength() {
      if (this.activeTab === 'ask') return this.filteredStudyRecords.length
      if (this.activeTab === 'practice') return this.filteredPracticeRecords.length
      return this.filteredChatRecords.length
    },
  },
  mounted() {
    this.isMounted = true
  },
  methods: {
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now.getTime() - date.getTime()
      const days = Math.floor(diff / 86400000)

      if (days === 0) return '今天'
      if (days === 1) return '昨天'
      if (days < 7) return `${days}天前`
      if (days < 30) return `${Math.floor(days / 7)}周前`
      return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
    },
    formatDuration(seconds) {
      if (seconds < 60) return `${seconds}秒`
      return `${Math.floor(seconds / 60)}分${seconds % 60}秒`
    },
    isWithinTimeFilter(timestamp) {
      if (this.timeFilter === 'all') return true
      const now = Date.now()
      const diff = now - timestamp
      if (this.timeFilter === '7d') return diff <= 7 * 86400000
      if (this.timeFilter === '30d') return diff <= 30 * 86400000
      return true
    },
    handleTabClick(tabId) {
      this.activeTab = tabId
      this.searchQuery = ''
      this.timeFilter = 'all'
    },
    toggleMenu(id) {
      this.openMenuId = this.openMenuId === id ? null : id
    },
    handleDelete(id) {
      if (this.activeTab === 'ask' && this.onDeleteStudyRecord) this.onDeleteStudyRecord(id)
      if (this.activeTab === 'practice' && this.onDeletePracticeRecord) this.onDeletePracticeRecord(id)
      if (this.activeTab === 'chat' && this.onDeleteChatRecord) this.onDeleteChatRecord(id)
      this.openMenuId = null
    },
    handleClear() {
      if (this.activeTab === 'ask' && this.onClearStudyRecords) this.onClearStudyRecords()
      if (this.activeTab === 'practice' && this.onClearPracticeRecords) this.onClearPracticeRecords()
      if (this.activeTab === 'chat' && this.onClearChatRecords) this.onClearChatRecords()
      this.showClearConfirm = false
    },
    handleNavigate(view, item) {
      if (this.onNavigate) this.onNavigate(view, item)
    },
    handleReloadChat(context) {
      if (this.onReloadChat) this.onReloadChat(context)
    },
    impactItems(impact) {
      return [
        { label: '士气', value: impact.morale },
        { label: '效率', value: impact.efficiency },
        { label: '保留', value: impact.retention },
      ]
    },
  },
}
</script>

<style scoped>
.tab-switch-enter-active,
.tab-switch-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tab-switch-enter {
  opacity: 0;
  transform: translateY(10px);
}

.tab-switch-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
  opacity: 0;
}
</style>
